import React, { useMemo } from 'react';
import { ALL_ID, ProfileV2ComponentAttributes } from '@loyalty/editor-components';
import { CurrencyAttributes } from '@loyalty/shared-common/gamification-api-client';
import { FormGroup } from '@mui/material';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SelectField } from '../../../../shared/forms/fields/SelectField';
import { SwitchField } from '../../../../shared/forms/fields/SwitchField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { useCurrencies } from '../../../../currency/CurrenciesContext';

const nameFormatOptions = [
  { id: 'fullname', name: 'John Doe' },
  { id: 'firstNameInitialLastname', name: 'John D.' },
  { id: 'onlyFirstname', name: 'John' },
  { id: 'fullInitials', name: 'JD.' },
];

type FormValue = TraitFormValue<ProfileV2ComponentAttributes, void>;

export function ProfileV2WidgetTraits() {
  const { t, i18n } = useTranslation();
  const { currencies } = useCurrencies();

  const { watch, control } = useFormContext<FormValue>();

  const currencyOptions = useMemo(
    () => [
      {
        id: ALL_ID,
        name: t('editor.profile.traits.currency.all'),
      },
      ...currencies
        .filter(value => !value.deletedAt)
        .map((currency: CurrencyAttributes) => ({
          id: currency.id,
          name: getLocalizedProperty(currency, 'name', i18n.language) || 'No name',
        })),
    ],
    [currencies, i18n.language, t],
  );

  const showProfileName = watch('attributes.showProfileName');
  const showCurrencyBalance = watch('attributes.showCurrencyBalance');

  return (
    <FormGroup>
      <Trait>
        <SwitchField<FormValue>
          name="attributes.showProfilePicture"
          label={t('editor.leaderboard.traits.showProfilePicture')}
        />
      </Trait>
      <Trait>
        <SwitchField<FormValue>
          name="attributes.showProfileName"
          label={t('editor.leaderboard.traits.showProfileName')}
        />
      </Trait>
      {showProfileName && (
        <Trait>
          <SelectField
            control={control}
            name="attributes.profileNameFormat"
            label={t('editor.leaderboard.traits.profileNameFormat')}
            options={nameFormatOptions}
          />
        </Trait>
      )}
      <Trait>
        <SwitchField<FormValue>
          name="attributes.showLevel"
          label={t('editor.profile.traits.showLevel')}
        />
      </Trait>
      <Trait>
        <SwitchField<FormValue>
          name="attributes.showCurrencyBalance"
          label={t('editor.profile.traits.showCurrencyBalance')}
        />
      </Trait>
      {showCurrencyBalance && (
        <Trait>
          <SelectField
            control={control}
            name="attributes.currencyId"
            label={t('editor.leaderboard.traits.currency')}
            options={currencyOptions}
          />
        </Trait>
      )}
    </FormGroup>
  );
}
